/**
 * CypSchoolRepManagementDialog
 * ============================
 *
 * Parent/carer-facing dialog for the school representative they nominated
 * on the CYP ADHD pathway. It is the guardian's equivalent of the "Manage
 * informants" dialog an adult ADHD or autism patient gets, scoped to the one
 * person a guardian is responsible for.
 *
 * What the parent can see
 *   - who the school representative is (name, role at school, school, email)
 *   - whether they have logged in yet, and how far through their four
 *     questionnaires they are (per-form bars plus an overall count)
 *
 * What the parent can do
 *   - resend the invitation email, but only while the school rep has not yet
 *     set up their account. Once they have logged in a new invite is useless
 *     (the link would just take them to a login they already have), so the
 *     action disappears rather than being offered and then refused.
 *
 * Deliberately NOT offered here: replacing or removing the school rep. Those
 * delete or archive questionnaire answers the clinical team may already be
 * relying on, and need their own confirmation UX. The clinic can do both from
 * the provider side today; a guardian who needs a change is told to contact
 * the clinic.
 *
 * Why this is not InformantManagementContent
 *   That component is driven by useInformantManagement, which reads the
 *   child's patient document and every informant's profile directly from
 *   Firestore. A guardian is not permitted to read either, so the shared
 *   hook cannot be reused from this seat. All data here arrives pre-fetched
 *   from the getCypSchoolRepStatus callable via useCypSchoolRepStatus; the
 *   only write is the resendWelcomeEmail callable, which has a guardian path
 *   that checks the target is this guardian's own nominee.
 *
 * Props
 *   isOpen      - whether the dialog is visible
 *   onClose     - close the dialog
 *   schoolRep   - the `schoolRep` object from the callable (see
 *                 cyp_school_rep_status.py build_school_rep_status)
 *   patientId   - the child's patient ID (required by resendWelcomeEmail)
 *   childName   - the child's first name for personalised copy
 *   onRefresh   - re-fetch school rep status after an action
 */
import React, { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { httpsCallable } from 'firebase/functions';
import { XIcon, MailIcon, CheckCircleIcon, ClockIcon, LoginIcon } from '@heroicons/react/outline';
import { functions } from '../../../../firebase';
import { getInformantStatus } from '../../../../hooks/useInformantManagement';
import { CYP_ADHD_SCHOOL_FORM_KEYS } from '../../../../constants/cypAdhdFormKeys';
import { clampPercentage } from '../../../../utils/formProgress';
import { formatDateShort } from '../../../../utils/timestampUtils';
import ActionRow from '../../../../components/informantManagement/ActionRow';
import InfoTooltip from '../../../../components/InfoTooltip';
import WarningPanel from '../../../../components/informantManagement/WarningPanel';
import ConfirmationScreen from '../../../../components/informantManagement/ConfirmationScreen';
import ButtonSpinner from '../../../../components/informantManagement/ButtonSpinner';

/**
 * Parent-facing titles for the school representative's four questionnaires.
 * Plain English at reading age 12; the provider-side clinical titles in
 * cypAdhdFormKeys.js are deliberately not reused because "Basic information"
 * means nothing to a parent and "SNAP-IV rating scale" needs a gloss.
 */
export const CYP_SCHOOL_REP_FORM_TITLES = Object.freeze({
  aboutYouAndConsent: 'About them and their consent',
  reasonForReferral: 'Reason for referral',
  educationHistory: 'Education',
  snapIV26Informant: 'ADHD symptoms at school (SNAP-IV)',
});

/** Fallback title for an unexpected key: split camelCase into words. */
function formTitle(formKey) {
  return CYP_SCHOOL_REP_FORM_TITLES[formKey]
    || formKey.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
}

/** Map resendWelcomeEmail error codes to copy a parent can act on. */
function resendErrorMessage(err) {
  const code = err?.code || '';
  if (code.includes('failed-precondition')) {
    return 'This school representative has already set up their account, so a new invitation is not needed.';
  }
  if (code.includes('permission-denied')) {
    return 'You can only resend the invitation to the school representative you nominated.';
  }
  return 'We could not resend the invitation. Please try again, or contact the clinic if the problem continues.';
}

const STATUS_ICONS = {
  completed: CheckCircleIcon,
  progress: ClockIcon,
  'logged-in': LoginIcon,
  invited: MailIcon,
};

export default function CypSchoolRepManagementDialog({
  isOpen,
  onClose,
  schoolRep,
  patientId,
  childName,
  onRefresh,
}) {
  // 'detail' is the default view; 'resend' is the confirm step; 'resent' is
  // the success screen. Kept as a tiny state machine so Back/Finish are
  // explicit rather than a stack of booleans.
  const [screen, setScreen] = useState('detail');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // Always reopen on the detail view, never on a stale confirm/success step.
  useEffect(() => {
    if (isOpen) {
      setScreen('detail');
      setSendError(null);
      setIsSending(false);
    }
  }, [isOpen]);

  const fullName = `${schoolRep?.firstName || ''} ${schoolRep?.lastName || ''}`.trim() || 'School representative';
  const status = schoolRep ? getInformantStatus(schoolRep) : null;
  const StatusIcon = status ? (STATUS_ICONS[status.icon] || MailIcon) : MailIcon;

  // Resend is only meaningful before first login, and only for a rep with a
  // login to set up. hasAuthAccount defaults true server-side.
  const canResend = Boolean(
    schoolRep && schoolRep.hasAuthAccount !== false && schoolRep.hasSetOwnPassword === false,
  );

  const handleResend = useCallback(async () => {
    if (!schoolRep?.uid || !patientId) return;
    setIsSending(true);
    setSendError(null);
    try {
      const resend = httpsCallable(functions, 'resendWelcomeEmail');
      await resend({ targetUserId: schoolRep.uid, patientId });
      setScreen('resent');
    } catch (err) {
      console.error('CypSchoolRepManagementDialog: resend failed', err);
      setSendError(resendErrorMessage(err));
    } finally {
      setIsSending(false);
    }
  }, [schoolRep, patientId]);

  const handleFinish = useCallback(() => {
    onRefresh?.();
    onClose();
  }, [onRefresh, onClose]);

  const formKeys = CYP_ADHD_SCHOOL_FORM_KEYS;
  const completedCount = typeof schoolRep?.completedCount === 'number'
    ? schoolRep.completedCount
    : formKeys.filter((k) => schoolRep?.formProgress?.[k]?.status === 'completed').length;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Dialog.Content
          className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 md:p-6 rounded-lg shadow-lg max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto z-50"
          aria-describedby="cyp-school-rep-dialog-description"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 z-10"
            aria-label="Close dialog"
          >
            <XIcon className="w-5 h-5" aria-hidden="true" />
          </button>

          <Dialog.Title className="text-lg font-bold mb-3 pr-6">
            {childName ? `${childName}'s school representative` : 'School representative'}
          </Dialog.Title>
          <VisuallyHidden.Root>
            <Dialog.Description id="cyp-school-rep-dialog-description">
              See who you nominated from the school, how far through their questionnaires they are,
              and resend their invitation if they have not logged in yet.
            </Dialog.Description>
          </VisuallyHidden.Root>

          {!schoolRep ? (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500">
                School representative details are not available right now. Please close this and try again.
              </p>
            </div>
          ) : screen === 'resent' ? (
            <ConfirmationScreen
              headline="Invitation sent"
              detail={`We have emailed ${fullName} at ${schoolRep.email} with a new link to set up their account.`}
              onFinish={handleFinish}
            />
          ) : screen === 'resend' ? (
            <div className="space-y-3">
              <div className="border-b border-gray-200 pb-2">
                <h3 className="text-sm font-semibold text-gray-900">Resend the invitation</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Send {fullName} a new email with a link to set up their account.
                </p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <dl className="grid gap-x-3 gap-y-1 text-xs" style={{ gridTemplateColumns: 'auto 1fr' }}>
                  <dt className="text-gray-600">To</dt>
                  <dd className="font-medium text-gray-800">{fullName}</dd>
                  <dt className="text-gray-600">Email</dt>
                  <dd className="font-medium text-gray-800 break-all">{schoolRep.email}</dd>
                </dl>
                {schoolRep.createdAt && (
                  <p className="text-xs text-gray-500 mt-2">
                    First invited on {formatDateShort(schoolRep.createdAt)}
                  </p>
                )}
              </div>

              <WarningPanel variant="info">
                It can take a few minutes to arrive. Ask them to check their junk or spam folder too.
              </WarningPanel>

              {sendError && <WarningPanel variant="error">{sendError}</WarningPanel>}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setSendError(null); setScreen('detail'); }}
                  disabled={isSending}
                  className="flex-1 px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isSending}
                  className={`flex-1 px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
                    isSending ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-600 hover:bg-violet-700'
                  }`}
                >
                  {isSending ? (
                    <span className="inline-flex items-center gap-2">
                      <ButtonSpinner />
                      Sending
                    </span>
                  ) : 'Send invitation'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Who they are */}
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{fullName}</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {[schoolRep.relationship, schoolRep.schoolName].filter(Boolean).join(' at ') || 'School representative'}
                    </p>
                    {schoolRep.email && (
                      <p className="text-xs text-gray-500 mt-0.5 break-all">{schoolRep.email}</p>
                    )}
                  </div>
                </div>
                {status && (
                  <p className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium ${status.color}`}>
                    <StatusIcon className="w-4 h-4" aria-hidden="true" />
                    {status.label}
                  </p>
                )}
              </div>

              {/* Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Their questionnaires</h3>
                  <span className="text-xs text-gray-600">
                    {completedCount} of {formKeys.length} complete
                  </span>
                </div>
                <ul className="space-y-2">
                  {formKeys.map((formKey) => {
                    const entry = schoolRep.formProgress?.[formKey] || {};
                    const pct = clampPercentage(entry.percentage ?? 0);
                    const done = entry.status === 'completed' || pct >= 100;
                    return (
                      <li key={formKey} className="border border-gray-200 rounded-md p-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-gray-800">{formTitle(formKey)}</span>
                          <span className={`text-xs font-medium ${done ? 'text-green-700' : 'text-gray-600'}`}>
                            {done ? 'Complete' : pct > 0 ? `${pct}%` : 'Not started'}
                          </span>
                        </div>
                        <div
                          className="mt-1.5 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden"
                          role="progressbar"
                          aria-label={`${formTitle(formKey)} progress`}
                          aria-valuenow={pct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div
                            className={`h-full rounded-full ${done ? 'bg-green-500' : 'bg-violet-500'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Actions — resend stays visible but greys out once the school
                  rep has logged in, with a tooltip explaining why. Matches the
                  Adult ADHD informant management pattern where greyed-out
                  actions with tooltips are preferred over removing them. */}
              <ActionRow
                icon={<MailIcon className="w-4 h-4" />}
                label="Resend the invitation"
                description={canResend
                  ? `${fullName} has not logged in yet. Send them a new link to set up their account.`
                  : `${fullName} has already set up their account.`}
                onClick={canResend ? () => setScreen('resend') : undefined}
                disabled={!canResend}
                trailing={!canResend ? (
                  <InfoTooltip
                    content="A welcome email can only be resent before the school representative has signed in and set up their password."
                    side="left"
                    maxWidth="220px"
                  />
                ) : undefined}
              />
              {!canResend && (
                <p className="text-xs text-gray-500">
                  If anything about the school representative needs to change, please contact the clinic.
                </p>
              )}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
